// src/pages/CustomerPortalAI.jsx
// Customer Portal AI Assistant — lets the customer ask questions about their
// own data (appointments, points, wallet, packages, profile) and get guidance
// on how to use the Customer Portal. Read-only: the AI cannot make changes on
// the customer's behalf. Design mirrors the Points & Rewards (LoyaltyProgram) page.
import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { CustomerPortalAgentService } from '../api/backendAiService';
import { getSalonConfig } from '../api/salonConfigService';
import '../styles/loyalty.css';
import '../styles/customer-ai-agent.css';

const getFontSizeValue = (fontSize) => {
  switch (fontSize) {
    case 'small': return '14px';
    case 'large': return '18px';
    default: return '16px';
  }
};

// ── Example prompts ───────────────────────────────────────────────────────────

const QUESTION_EXAMPLES = [
  'When is my next appointment?',
  'How many loyalty points do I have?',
  'Do I have any vouchers I can use?',
  'What is my wallet balance?',
  'Show me my upcoming appointments',
  'How many sessions are left on my package?',
  'Has my wallet top-up been verified?',
  "What are this salon's business hours?",
];

const ACTION_EXAMPLES = [
  'How do I book an appointment?',
  'How do I cancel or reschedule an appointment?',
  'How do I top up my wallet?',
  'How do I use a voucher?',
  'How do I update my contact details?',
  'How do I view my transaction history?',
  'Where can I see my past appointments?',
  'How do I opt out of marketing emails?',
];

// ── Component ─────────────────────────────────────────────────────────────────

function CustomerPortalAI() {
  const { salonId } = useParams();
  const { currentUser } = useCustomerAuth();

  const [messages, setMessages] = useState([{
    id: 'welcome',
    type: 'ai',
    content: "Hi! I'm your personal AI assistant for this portal. I can answer questions about your appointments, loyalty points, vouchers, wallet, packages, and profile — and guide you to the right page for anything you'd like to do. What can I help you with today?",
    timestamp: new Date()
  }]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [limitReached, setLimitReached] = useState(false);

  const serviceRef = useRef(null);
  const recognitionRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Apply salon branding
  useEffect(() => {
    if (!salonId) return;
    getSalonConfig(salonId).then(cfg => {
      if (cfg?.branding) {
        const root = document.documentElement;
        if (cfg.branding.colors) {
          root.style.setProperty('--salon-primary', cfg.branding.colors.primary || '#E64C31');
          root.style.setProperty('--salon-secondary', cfg.branding.colors.secondary || '#FFBDB1');
          root.style.setProperty('--salon-accent', cfg.branding.colors.accent || '#F6F6F6');
          root.style.setProperty('--salon-background', cfg.branding.colors.background || '#FFFFFF');
          root.style.setProperty('--salon-text', cfg.branding.colors.text || '#333333');
        }
        if (cfg.branding.typography) {
          root.style.setProperty('--salon-heading-font', cfg.branding.typography.headingFont || 'Familjen Grotesk, sans-serif');
          root.style.setProperty('--salon-body-font', cfg.branding.typography.bodyFont || 'Wix Madefor Text, sans-serif');
          root.style.setProperty('--salon-font-size', getFontSizeValue(cfg.branding.typography.fontSize));
        }
      }
    }).catch(() => {});
  }, [salonId]);

  // Initialise the agent service
  useEffect(() => {
    if (salonId) serviceRef.current = new CustomerPortalAgentService(salonId);
  }, [salonId]);

  // Scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, isLoading]);

  // Voice recognition setup
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(prev => (prev ? `${prev} ${transcript}` : transcript));
      setIsListening(false);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
    return () => recognition.abort();
  }, []);

  const handleVoiceInput = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser.');
      return;
    }
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const addMessage = (msg) =>
    setMessages(prev => [...prev, { id: `${Date.now()}-${Math.random()}`, timestamp: new Date(), ...msg }]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading || limitReached || !serviceRef.current) return;

    addMessage({ type: 'user', content: text });
    setInput('');
    setIsLoading(true);

    try {
      const res = await serviceRef.current.sendMessage(text);
      addMessage({ type: 'ai', content: res.message });
      if (res.dailyUsage?.limitExceeded) setLimitReached(true);
    } catch (error) {
      console.error('Customer Portal AI error:', error);
      const content = error?.status === 429
        ? 'The daily AI usage limit has been reached. Please try again tomorrow.'
        : error?.status === 403
          ? 'AI Assistant is not available for this salon.'
          : error?.status === 401
            ? 'Session expired. Please refresh the page and try again.'
            : error?.status === 404
              ? 'Customer record not found. Please ensure you are logged in.'
              : `Something went wrong: ${error?.message || 'Unknown error'}. Please try again in a moment.`;
      addMessage({ type: 'system', content });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (t) => t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const userInitial = (currentUser?.displayName || currentUser?.email || 'C').charAt(0).toUpperCase();

  return (
    <div className="cpa-container">

      {/* Page header — matches .ap-page-header in loyalty.css */}
      <div className="cpa-page-header">
        <h1 className="cpa-page-title">AI Assistant</h1>
        <p className="cpa-page-desc">
          Ask me anything about your bookings, points, wallet, packages or profile,
          or ask me how to use any part of this portal.
        </p>
      </div>

      {/* Example prompts */}
      <div className="cpa-examples-card">
        <h3 className="cpa-examples-heading">Ask me about your account</h3>
        <div className="cpa-example-chips">
          {QUESTION_EXAMPLES.map(ex => (
            <button
              key={ex}
              className="cpa-example-chip"
              onClick={() => setInput(ex)}
            >
              {ex}
            </button>
          ))}
        </div>

        <h3 className="cpa-examples-heading" style={{ marginTop: 16 }}>How do I…</h3>
        <div className="cpa-example-chips">
          {ACTION_EXAMPLES.map(ex => (
            <button
              key={ex}
              className="cpa-example-chip"
              onClick={() => setInput(ex)}
            >
              {ex}
            </button>
          ))}
        </div>
      </div>

      {/* Shared daily token limit notice */}
      <div className="cpa-limit-note">
        Customers and Staff share the same limit on the maximum tokens that can be used
        per day. Contact your salon's staff in the event that you are unable to use the
        AI feature here in the Customer Portal.
      </div>

      {/* Chat interface */}
      <div className="cpa-chat-card">
        <div className="cpa-messages">
          {messages.map(m => (
            <div key={m.id} className={`cpa-message cpa-message--${m.type}`}>
              {m.type === 'ai' && <div className="cpa-avatar">🤖</div>}
              {m.type === 'user' && <div className="cpa-avatar">{userInitial}</div>}
              {m.type === 'system' && <div className="cpa-avatar">ℹ️</div>}
              <div className="cpa-bubble">
                <ReactMarkdown>{m.content}</ReactMarkdown>
                <div className="cpa-msg-time">{formatTime(m.timestamp)}</div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="cpa-message cpa-message--ai">
              <div className="cpa-avatar">🤖</div>
              <div className="cpa-bubble">
                <div className="cpa-typing">
                  <span /><span /><span />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="cpa-input-area">
          <textarea
            ref={textareaRef}
            className="cpa-textarea"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              limitReached
                ? 'Daily AI usage limit reached. Resets at midnight.'
                : 'Ask me anything about your account… (Enter to send, Shift+Enter for new line)'
            }
            rows={3}
            disabled={isLoading || limitReached}
          />
          <div className="cpa-input-row">
            <button
              className={`cpa-voice-btn${isListening ? ' cpa-voice-btn--listening' : ''}`}
              onClick={handleVoiceInput}
              disabled={isLoading || limitReached}
              title={isListening ? 'Stop listening' : 'Voice input'}
            >
              {isListening ? '⏹ Stop' : '🎤 Voice'}
            </button>
            <span className="cpa-hint">I can only see your own data</span>
            <button
              className="cpa-send-btn"
              onClick={handleSend}
              disabled={isLoading || !input.trim() || limitReached}
            >
              {isLoading ? 'Thinking…' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomerPortalAI;
